import React, { useState, useRef, useEffect } from 'react';
import { Check } from 'lucide-react';
import { plans, plansB } from '../../constants/content';
import type { Plan as PlanType } from '../../types';

const HorizontalCarousel: React.FC<{ ariaLabel: string; children: React.ReactNode; }> = ({ ariaLabel, children }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const childCount = React.Children.count(children);

  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current) return;
      const container = scrollContainerRef.current;
      const center = container.scrollLeft + container.clientWidth / 2;

      let closestIndex = 0;
      let minDistance = Infinity;

      Array.from(container.children).forEach((child, index) => {
        const htmlChild = child as HTMLElement;
        const childCenter = htmlChild.offsetLeft + htmlChild.offsetWidth / 2;
        const distance = Math.abs(childCenter - center);

        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      });

      setActiveIndex(closestIndex);
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      handleScroll();
    }

    return () => {
      container?.removeEventListener('scroll', handleScroll);
    };
  }, [children]);

  const scrollTo = (index: number) => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const child = container.children[index] as HTMLElement;

    if (child) {
      const childCenter = child.offsetLeft + child.offsetWidth / 2;
      const scrollLeft = childCenter - container.clientWidth / 2;

      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="relative flex flex-col gap-6" role="region" aria-label={ariaLabel} aria-roledescription="carousel">
      <div className="relative">
        <div className="pointer-events-none absolute left-0 top-0 z-10 h-full w-8 bg-gradient-to-r from-white via-white/60 to-transparent" />
        <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-8 bg-gradient-to-l from-white via-white/60 to-transparent" />

        <div
          ref={scrollContainerRef}
          className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4 px-4 sm:px-8 [&::-webkit-scrollbar]:hidden"
          style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {React.Children.map(children, (child, index) => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child as React.ReactElement<any>, {
                id: `carousel-item-${index}`,
                role: 'group',
                'aria-roledescription': 'slide',
                'aria-label': `${index + 1} de ${childCount}`
              });
            }
            return child;
          })}
        </div>
      </div>

      <div className="flex justify-center gap-2" role="tablist" aria-label="Controles de diapositivas">
        {Array.from({ length: childCount }).map((_, index) => (
          <button
            key={index}
            role="tab"
            aria-selected={index === activeIndex}
            aria-label={`Ir a diapositiva ${index + 1}`}
            aria-controls={`carousel-item-${index}`}
            onClick={() => scrollTo(index)}
            className={`h-2 rounded-full transition-all duration-300 ${index === activeIndex
                ? 'w-8 bg-contessa-600'
                : 'w-2 bg-contessa-200 hover:bg-contessa-300'
              }`}
          />
        ))}
      </div>
    </div>
  );
};

const PlanCard: React.FC<{ plan: PlanType; variant?: 'primary' | 'secondary'; className?: string; }> = ({ plan, variant = 'primary', className }) => {
  const isFeatured = Boolean(plan.isPopular && variant === 'primary');
  const hasPromotionBadge = Boolean(plan.isPromotion);
  const displayPrice = plan.previousPriceDolars ?? plan.priceDolars;
  const baseStyles = variant === 'primary'
    ? 'bg-white/95 border border-contessa-100 shadow-[0_18px_45px_-30px_rgba(132,90,85,0.7)]'
    : 'bg-white/90 border border-contessa-50 shadow-[0_14px_35px_-30px_rgba(132,90,85,0.6)]';

  const handleWhatsAppClick = () => {
    const message = `Hola Marha, me interesa el plan ${plan.name}${plan.subtitle ? ` (${plan.subtitle})` : ''}, me puedes dar más información?`;
    const whatsappUrl = `https://wa.me/59898816513?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <article
      className={`relative flex h-full flex-col overflow-hidden snap-center w-[78vw] min-w-[78vw] shrink-0 rounded-3xl p-6 backdrop-blur transition-transform duration-300 hover:-translate-y-1 lg:w-full lg:min-w-0 ${baseStyles} ${className ?? ''}`}
    >
      {isFeatured && (
        <span className="absolute top-0 right-0 rounded-bl-2xl bg-contessa-600 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-white">
          Recomendado
        </span>
      )}
      <div className="flex items-start justify-between gap-4">
        <div>
          {plan.subtitle && (
            <span className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-widest ${isFeatured ? 'bg-contessa-100 text-contessa-700' : 'bg-contessa-50 text-contessa-600'}`}>
              {plan.subtitle}
            </span>
          )}
          <h3 className="mt-3 text-2xl font-serif font-bold text-contessa-900">
            {plan.name}
          </h3>
        </div>
      </div>

      <div className="mt-4">
        <div className="flex flex-col items-start gap-3 lg:flex-row lg:items-center">
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="text-4xl font-serif font-semibold text-contessa-900">
              {displayPrice}
            </span>
          </div>
        </div>
        <p className="mt-3 text-sm leading-relaxed whitespace-pre-line text-contessa-700">
          {plan.description.trim()}
        </p>
      </div>

      <div className="mt-6 flex-1">
        <ul className="space-y-3 text-sm text-contessa-700">
          {plan.features.map((feature) => (
            <li key={feature} className="flex gap-3">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-contessa-100 text-contessa-500">
                <Check size={14} />
              </span>
              <span className="leading-relaxed">{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      <button
        className={`mt-6 w-full rounded-full px-6 py-3 text-sm font-semibold transition-colors duration-300 ${isFeatured ? 'bg-contessa-600 text-white hover:bg-contessa-700' : 'bg-contessa-100 text-contessa-800 hover:bg-contessa-200'}`}
        onClick={handleWhatsAppClick}
      >
        ¡Quiero más info!
      </button>
    </article>
  );
};

const Plans: React.FC = () => {
  return (
    <section id="planes" className="relative overflow-hidden py-20">
      <div className="pointer-events-none absolute left-1/2 top-0 h-80 w-[140vw] -translate-x-1/2 rounded-[999px] bg-contessa-50/80 blur-[140px]" />
      <div className="pointer-events-none absolute -bottom-24 right-[-40vw] h-80 w-[80vw] rounded-full bg-contessa-100/70 blur-[130px]" />

      <div className="relative container mx-auto px-4">
        <div className="w-24 h-1 bg-contessa-300 mx-auto mb-8"></div>
        <div className="mx-auto max-w-xl text-center">
          <span className="inline-flex items-center justify-center rounded-full bg-contessa-50 px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-contessa-500">
            Planes
          </span>
        </div>

        <div className="mt-12">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="mt-5 text-3xl font-serif font-bold text-contessa-900 sm:text-4xl">
                Preparación para el nacimiento
               </h2>
              <p className="mt-1 text-sm text-contessa-700">
                Elige el acompañamiento que mejor se adapte a tus necesidades y preferencias.
              </p>
            </div>
          </div>

          <div className="mt-6 lg:hidden w-screen ml-[calc(50%-50vw)]">
            <HorizontalCarousel ariaLabel="Planes de preparación para el nacimiento">
              {plans.map((plan) => (
                <PlanCard key={plan.id} plan={plan} />
              ))}
            </HorizontalCarousel>
          </div>

          <div className="mt-8 hidden gap-6 lg:grid lg:grid-cols-2">
            {plans.map((plan) => (
              <PlanCard key={plan.id} plan={plan} className="min-w-0" />
            ))}
          </div>
        </div>

        <div className="mt-16">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="mt-5 text-3xl font-serif font-bold text-contessa-900 sm:text-4xl">
                Acompañamientos especializados
              </h2>
              <p className="mt-1 text-sm text-contessa-700">
                Apoyo específico para el puerperio y el duelo perinatal.
              </p>
            </div>
          </div>

          <div className="mt-6 lg:hidden w-screen ml-[calc(50%-50vw)]">
            <HorizontalCarousel ariaLabel="Planes de acompañamiento especializado">
              {plansB.map((plan) => (
                <PlanCard key={plan.id} plan={plan} variant="secondary" />
              ))}
            </HorizontalCarousel>
          </div>

          <div className="mt-8 hidden gap-6 lg:grid lg:grid-cols-2">
            {plansB.map((plan) => (
              <PlanCard key={plan.id} plan={plan} variant="secondary" className="min-w-0" />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Plans;